import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import PageMeta from '@/components/base/PageMeta';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.id !== comic.id && (c.category === comic.category || c.tags.some((t) => comic.tags.includes(t))))
      .slice(0, 6);
  }, [comic]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <ScrollToTop />
        <main className="min-h-screen bg-background-50 pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold font-heading text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">该漫画可能已被下架或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === comic.category);
  const mockChapters = Array.from({ length: Math.min(20, comic.chapters) }, (_, i) => ({
    id: comic.chapters - i,
    title: `第${comic.chapters - i}话${i === 0 ? '（最新）' : ''}`,
    date: new Date(2026, 7, 5 - i).toISOString().split('T')[0],
  }));

  return (
    <>
      <PageMeta
        title={`${comic.title} - 韩国漫画 - 韩漫网`}
        description={`${comic.title}，作者${comic.author}。${comic.description.slice(0, 80)}... 韩漫网免费在线观看韩国漫画。`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: comic.title,
          author: { '@type': 'Person', name: comic.author },
          description: comic.description,
          datePublished: '2026-01-01',
          dateModified: comic.updateTime,
          image: comic.cover,
          publisher: { '@type': 'Organization', name: '韩漫网', url: 'https://hanmanwang.com/' },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '韩漫网首页', item: 'https://hanmanwang.com/' },
              { '@type': 'ListItem', position: 2, name: category?.name || '漫画', item: `https://hanmanwang.com/category/${comic.category}` },
              { '@type': 'ListItem', position: 3, name: comic.title },
            ],
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://hanmanwang.com/comic/${comic.id}`,
          },
        }}
      />
      <Navbar />
      <ScrollToTop />
      <main className="min-h-screen bg-background-50 pt-16">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || '漫画', href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          {/* Comic Info */}
          <article className="flex flex-col md:flex-row gap-6 md:gap-8 py-6">
            <div className="w-full md:w-[280px] shrink-0">
              <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-100">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  className="w-full h-full object-cover object-top"
                  title={`${comic.title} - 韩国漫画`}
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-3">
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span className={`text-xs px-2 py-0.5 rounded font-medium whitespace-nowrap ${
                  comic.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500 text-sm"></i>
                  <span className="text-sm font-semibold text-foreground-900">{comic.rating}</span>
                </div>
                <span className="text-xs text-foreground-400">|</span>
                <span className="text-xs text-foreground-500">{comic.views} 次阅读</span>
                <span className="text-xs text-foreground-400">|</span>
                <span className="text-xs text-foreground-500">共 {comic.chapters} 话</span>
                <span className="text-xs text-foreground-400">|</span>
                <span className="text-xs text-foreground-500">更新于 {comic.updateTime}</span>
              </div>

              <div className="flex flex-wrap gap-2 mb-5">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs font-medium bg-background-100 text-foreground-600 rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100 rounded-xl p-4 mb-5">
                <h2 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex flex-wrap gap-3">
                <button className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2">
                  <i className="ri-play-circle-line"></i> 开始阅读
                </button>
                <button className="px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 rounded-full text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2">
                  <i className="ri-bookmark-line"></i> 收藏
                </button>
              </div>
            </div>
          </article>

          {/* Chapter List */}
          <section className="py-6 border-t border-background-200/70">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold font-heading text-foreground-950">章节列表</h2>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 text-xs font-medium bg-background-100 text-foreground-600 rounded-full hover:bg-background-200 transition-colors whitespace-nowrap">
                  正序
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {mockChapters.map((chapter) => (
                <a
                  key={chapter.id}
                  href="#"
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors group"
                >
                  <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {chapter.title}
                  </span>
                  <span className="text-xs text-foreground-400 ml-2 shrink-0 whitespace-nowrap">{chapter.date}</span>
                </a>
              ))}
            </div>
            {comic.chapters > 20 && (
              <div className="text-center mt-4">
                <button className="px-5 py-2 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">
                  查看更多章节 <i className="ri-arrow-down-s-line"></i>
                </button>
              </div>
            )}
          </section>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section className="py-8 border-t border-background-200/70">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold font-heading text-foreground-950">相关推荐</h2>
                <Link
                  to={`/category/${comic.category}`}
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap"
                >
                  更多 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4" data-product-shop="">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} {...c} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}